import {
  createSlice,
  createAsyncThunk,
  isPending,
  isFulfilled,
  isRejected,
} from "@reduxjs/toolkit";
import http from "../../utils/axios";

// Fetch all subcategories
export const fetchSubCategories = createAsyncThunk(
  "subCategories/fetchSubCategories",
  async ({ page = 1, per_page = 15, status, search, category_uuid } = {}) => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("per_page", per_page);
    if (status !== undefined) params.append("status", status);
    if (search) params.append("search", search);
    if (category_uuid) params.append("category_uuid", category_uuid);
    
    const res = await http.get(`/dashboard/sub-categories?${params.toString()}`);
    return res;
  }
);

// Create subcategory
export const createSubCategory = createAsyncThunk(
  "subCategories/createSubCategory",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await http.post("/dashboard/sub-categories", payload);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to create subcategory"
      );
    }
  }
);

// Fetch subcategory by UUID
export const fetchSubCategoryById = createAsyncThunk(
  "subCategories/fetchSubCategoryById",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.get(`/dashboard/sub-categories/${uuid}`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch subcategory details"
      );
    }
  }
);

// Update subcategory
export const updateSubCategory = createAsyncThunk(
  "subCategories/updateSubCategory",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      const res = await http.post(`/dashboard/sub-categories/${uuid}`, data);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update subcategory"
      );
    }
  }
);

// Delete subcategory
export const deleteSubCategory = createAsyncThunk(
  "subCategories/deleteSubCategory",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.delete(`/dashboard/sub-categories/${uuid}`);
      return { uuid, data: res };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to delete subcategory"
      );
    }
  }
);

// Enable subcategory
export const enableSubCategory = createAsyncThunk(
  "subCategories/enableSubCategory",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.patch(`/dashboard/sub-categories/${uuid}/enable`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to enable subcategory"
      );
    }
  }
);

// Disable subcategory
export const disableSubCategory = createAsyncThunk(
  "subCategories/disableSubCategory",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.patch(`/dashboard/sub-categories/${uuid}/disable`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to disable subcategory"
      );
    }
  }
);

// Toggle subcategory status
export const toggleSubCategoryStatus = createAsyncThunk(
  "subCategories/toggleSubCategoryStatus",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.patch(`/dashboard/sub-categories/${uuid}/toggle-status`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to toggle subcategory status"
      );
    }
  }
);

// Update subcategory icon
export const updateSubCategoryIcon = createAsyncThunk(
  "subCategories/updateSubCategoryIcon",
  async ({ uuid, icon }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("icon", icon);
      
      const res = await http.post(`/dashboard/sub-categories/${uuid}/update-icon`, formData);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update subcategory icon"
      );
    }
  }
);

// Remove subcategory icon
export const removeSubCategoryIcon = createAsyncThunk(
  "subCategories/removeSubCategoryIcon",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.delete(`/dashboard/sub-categories/${uuid}/remove-icon`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to remove subcategory icon"
      );
    }
  }
);

const subCategoriesSlice = createSlice({
  name: "subCategories",
  initialState: {
    subCategories: [],
    pagination: null,
    subCategory: null,
    subCategoryCreateStatus: "idle",
    subCategoryUpdateStatus: "idle",
    subCategoryDeleteStatus: "idle",
    subCategoryFetchStatus: "idle",
    subCategoryStatusChangeStatus: "idle",
    subCategoryIconUpdateStatus: "idle",
    loading: false,
    error: null,
  },
  reducers: {
    resetSubCategoryStatuses: (state) => {
      state.subCategoryCreateStatus = "idle";
      state.subCategoryUpdateStatus = "idle";
      state.subCategoryDeleteStatus = "idle";
      state.subCategoryStatusChangeStatus = "idle";
      state.subCategoryIconUpdateStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch subcategories
      .addCase(fetchSubCategories.fulfilled, (state, action) => {
        state.subCategories = action.payload.data;
        state.pagination = action.payload.pagination;
      })

      // Create subcategory
      .addCase(createSubCategory.pending, (state) => {
        state.subCategoryCreateStatus = "loading";
      })
      .addCase(createSubCategory.fulfilled, (state, action) => {
        state.subCategoryCreateStatus = "success";
        state.subCategory = action.payload.data;
      })
      .addCase(createSubCategory.rejected, (state) => {
        state.subCategoryCreateStatus = "failed";
      })

      // Fetch subcategory by ID
      .addCase(fetchSubCategoryById.pending, (state) => {
        state.subCategoryFetchStatus = "loading";
      })
      .addCase(fetchSubCategoryById.fulfilled, (state, action) => {
        state.subCategoryFetchStatus = "success";
        state.subCategory = action.payload.data;
      })
      .addCase(fetchSubCategoryById.rejected, (state) => {
        state.subCategoryFetchStatus = "failed";
        state.subCategory = null;
      })

      // Update subcategory
      .addCase(updateSubCategory.pending, (state) => {
        state.subCategoryUpdateStatus = "loading";
      })
      .addCase(updateSubCategory.fulfilled, (state, action) => {
        state.subCategoryUpdateStatus = "success";
        const index = state.subCategories.findIndex(
          (subCat) => subCat.uuid === action.meta.arg.uuid
        );
        if (index !== -1) {
          state.subCategories[index] = action.payload.data;
        }
        state.subCategory = action.payload.data;
      })
      .addCase(updateSubCategory.rejected, (state) => {
        state.subCategoryUpdateStatus = "failed";
      })

      // Delete subcategory
      .addCase(deleteSubCategory.pending, (state) => {
        state.subCategoryDeleteStatus = "loading";
      })
      .addCase(deleteSubCategory.fulfilled, (state, action) => {
        state.subCategoryDeleteStatus = "success";
        state.subCategories = state.subCategories.filter(
          (subCat) => subCat.uuid !== action.payload.uuid
        );
      })
      .addCase(deleteSubCategory.rejected, (state) => {
        state.subCategoryDeleteStatus = "failed";
      })

      // Toggle status
      .addCase(toggleSubCategoryStatus.pending, (state) => {
        state.subCategoryStatusChangeStatus = "loading";
      })
      .addCase(toggleSubCategoryStatus.fulfilled, (state, action) => {
        state.subCategoryStatusChangeStatus = "success";
        const index = state.subCategories.findIndex(
          (subCat) => subCat.uuid === action.meta.arg
        );
        if (index !== -1) {
          state.subCategories[index] = action.payload.data;
        }
      })
      .addCase(toggleSubCategoryStatus.rejected, (state) => {
        state.subCategoryStatusChangeStatus = "failed";
      })

      // Update icon
      .addCase(updateSubCategoryIcon.pending, (state) => {
        state.subCategoryIconUpdateStatus = "loading";
      })
      .addCase(updateSubCategoryIcon.fulfilled, (state, action) => {
        state.subCategoryIconUpdateStatus = "success";
        const index = state.subCategories.findIndex(
          (subCat) => subCat.uuid === action.meta.arg.uuid
        );
        if (index !== -1) {
          state.subCategories[index] = action.payload.data;
        }
        if (state.subCategory?.uuid === action.meta.arg.uuid) {
          state.subCategory = action.payload.data;
        }
      })
      .addCase(updateSubCategoryIcon.rejected, (state) => {
        state.subCategoryIconUpdateStatus = "failed";
      })

      // Remove icon
      .addCase(removeSubCategoryIcon.fulfilled, (state, action) => {
        const index = state.subCategories.findIndex(
          (subCat) => subCat.uuid === action.meta.arg
        );
        if (index !== -1) {
          state.subCategories[index] = action.payload.data;
        }
        if (state.subCategory?.uuid === action.meta.arg) {
          state.subCategory = action.payload.data;
        }
      })

      .addMatcher(isPending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addMatcher(isFulfilled, (state) => {
        state.loading = false;
      })
      .addMatcher(isRejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { resetSubCategoryStatuses } = subCategoriesSlice.actions;
export default subCategoriesSlice.reducer;
